import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { map, catchError, lastValueFrom } from 'rxjs';

import { GlobalFiltersService } from '@app/root/app/shared';

import { PagedModel } from '@app/root/app/shared';
import { ApprovalViewModel } from '@app/root/app/shared';
import { EntityDocumentViewModel } from '@app/root/app/shared';

import { handleHttpError } from '../../functions';
import { DocumentSubjectCodes } from '@app/constants';
import { Environment } from '@app/environment';

@Injectable({
	providedIn: 'root',
})
export class ApprovalsService {
	private readonly baseUrl = `${Environment.apiBaseUrl}/approvals`;

	private httpClient: HttpClient = inject(HttpClient);
	private globalFiltersService: GlobalFiltersService = inject(GlobalFiltersService);

	private pageSize = 30;

	getApprovals$(
		leagueID: number | null,
		teamID: number | null,
		teamPlayerID: number | null,
		teamCoachID: number | null,
		documentStatusIDs: number[] | null,
		submissionDateFrom: string | null,
		submissionDateTo: string | null,
		submittedByUserID: number | null,
		documentTypeIDs: number[] | null,
		searchText: string | null,
		sortField: string,
		sortDirection: string,
		pageNumber: number,
	) {
		let httpParams = new HttpParams().appendAll({
			regionId: '',
			yearId: this.globalFiltersService.getLeagueYearID() ?? '',
			searchText: searchText || '',
			leagueId: leagueID || '',
			teamId: teamID || '',
			teamPlayerId: teamPlayerID || '',
			teamCoachId: teamCoachID || '',
			submissionDateFrom: submissionDateFrom || '',
			submissionDateTo: submissionDateTo || '',
			submittedByUserId: submittedByUserID || '',
			sortOrderField: sortField,
			sortOrderDirection: sortDirection,
			pageIndex: pageNumber,
		});

		// Append array params only if they have values (repeat the key for each element)
		if (documentStatusIDs && documentStatusIDs.length > 0) {
			documentStatusIDs.forEach(id => {
				httpParams = httpParams.append('documentStatusIds', id.toString());
			});
		}

		if (documentTypeIDs && documentTypeIDs.length > 0) {
			documentTypeIDs.forEach(id => {
				httpParams = httpParams.append('documentTypeIds', id.toString());
			});
		}

		return this.httpClient.get<PagedModel<ApprovalViewModel>>(`${this.baseUrl}/documents`, { params: httpParams })
			.pipe(
				map((pagedApprovals) => {
					const pagedApprovalDocs: PagedModel<EntityDocumentViewModel> =
					{
						pageData: [],
						totalRecords: pagedApprovals?.totalRecords || 0,
						pageSize: this.pageSize,
						pageIndex: pageNumber,
					};

					pagedApprovals.pageData.forEach((approval) => {
						pagedApprovalDocs.pageData.push(
							this.mapApprovalToEntityDocumentViewMode(approval),
						);
					});

					return pagedApprovalDocs;
				}),
				catchError(handleHttpError),
			);
	}

	getApprovals(
		leagueID: number | null,
		teamID: number | null,
		teamPlayerID: number | null,
		teamCoachID: number | null,
		documentStatusIDs: number[] | null,
		submissionDateFrom: string | null,
		submissionDateTo: string | null,
		submittedByUserID: number | null,
		documentTypeIDs: number[] | null,
		searchText: string | null,
		sortField: string,
		sortDirection: string,
		pageNumber: number,
	) {
		return lastValueFrom(
			this.getApprovals$(
				leagueID,
				teamID,
				teamPlayerID,
				teamCoachID,
				documentStatusIDs,
				submissionDateFrom,
				submissionDateTo,
				submittedByUserID,
				documentTypeIDs,
				searchText,
				sortField,
				sortDirection,
				pageNumber,
			),
		);
	}

	private mapApprovalToEntityDocumentViewMode(approval: ApprovalViewModel): EntityDocumentViewModel {
		return {
			regionId: 0,
			leagueYearId: approval.year,
			leagueId: approval.leagueId,
			leagueName: approval.organizationLeagueName,
			teamId: approval.teamId,
			teamName: approval.teamName,
			playerTeamId: approval.documentSubjectId === DocumentSubjectCodes.PlayerDocument ? approval.playerCoachId : null,
			playerName: approval.documentSubjectId === DocumentSubjectCodes.PlayerDocument ? approval.playerCoachName : null,
			coachId: approval.documentSubjectId === DocumentSubjectCodes.CoachDocument ? approval.playerCoachId : null,
			coachName: approval.documentSubjectId === DocumentSubjectCodes.CoachDocument ? approval.playerCoachName : null,
			documentSubjectId: approval.documentSubjectId,
			documentSubjectName: approval.documentSubjectName,
			documentId: approval.documentId,
			documentName: approval.documentName,
			documentInstanceId: approval.documentInstanceId,
			downloadable: approval.downloadable,
			downloadableFileId: approval.downloadableFileId,
			downloadableFileType: approval.downloadableFileType,
			documentSubmissionID: approval.submissionId,
			uploadable: true,
			uploadedFileId: approval.uploadedFileId,
			uploadedFileType: approval.uploadedFileType,
			uploaderName: approval.submittedByEmployeeName,
			uploadedDateTime: approval.dateSubmitted,
			required: approval.required,
			statusId: approval.statusId,
			statusName: approval.statusName,
			statusNotes: approval.statusNotes,
			version: approval.version,
			isCurrentVersion: true,
			olderVersions: null,
		} as EntityDocumentViewModel;
	}

	approveDocument(submissionID: number) {
		const httpParams = new HttpParams().appendAll({
			regionId: '',
			yearId: this.globalFiltersService.getLeagueYearID() ?? '',
		});

		return lastValueFrom(
			this.httpClient
				.post<ApprovalViewModel>(
					`${this.baseUrl}/documents/${submissionID}/approve`,
					null,
					{ params: httpParams },
				)
				.pipe(
					map(approval => this.mapApprovalToEntityDocumentViewMode(approval)),
					catchError(handleHttpError)
				)
		);
	}

	rejectDocument(submissionID: number, reason: string) {
		const httpParams = new HttpParams().appendAll({
			rejectionNotes: reason,
			regionId: '',
			yearId: this.globalFiltersService.getLeagueYearID() ?? '',
		});

		return lastValueFrom(
			this.httpClient
				.post<ApprovalViewModel>(
					`${this.baseUrl}/documents/${submissionID}/reject`,
					null,
					{ params: httpParams },
				)
				.pipe(
					map(approval => this.mapApprovalToEntityDocumentViewMode(approval)),
					catchError(handleHttpError)
				)
		);
	}
}
